
import {
    Component,
    EventEmitter,
    Input,
    Output,
    forwardRef
} from '@angular/core';

import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';

/**
 * Monotonically increasing integer used to auto-generate unique ids for checkbox components.
 */
let nextId = 0;

/**
 * Provider Expression that allows sky-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 */
// tslint:disable:no-forward-ref no-use-before-declare
export const SKY_CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SkyCheckboxComponent),
  multi: true
};

// A simple change event emitted by the SkyCheckbox component.
export class SkyCheckboxChange {
  public source: SkyCheckboxComponent;
  public checked: boolean;
}
// tslint:enable


export class SkyCheckboxComponent implements ControlValueAccessor {

  /**
   * Hidden label for screen readers.
   */
  
  public label: string = '';

  /**
   * Id of label for the checkbox.
   */
  
  public labelledBy: string;

  
  public id: string = `sky-checkbox-${++nextId}`;

  
  public disabled: boolean = false;

  
  public tabindex: number = 0;

  
  public name: string = `sky-checkbox-${++nextId}`;

  
  public change: EventEmitter<SkyCheckboxChange> = new EventEmitter<SkyCheckboxChange>();

  private _checked: boolean = false;

  public get inputId(): string {
    return `input-${this.id}`;
  }

  /** Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor. */
  /*istanbul ignore next */
  public onTouched: () => any = () => {};

  
  public get checked() {
    return this._checked;
  }

  public set checked(checked: boolean) {
    if (checked !== this.checked) {
      this._checked = checked;
    }
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  public writeValue(value: any) {
    this.checked = !!value;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  public registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Event handler for checkbox input element.
   * Toggles checked state if element is not disabled.
   */
  public onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();

    if (!this.disabled) {
      this._toggle();
      this._emitChangeEvent();
    }
  }

  public onInputBlur() {
    this.onTouched();
  }

  private _controlValueAccessorChangeFn: (value: any) => void = (value) => {};

  private _emitChangeEvent() {
    let event = new SkyCheckboxChange();
    event.source = this;
    event.checked = this._checked;

    this._controlValueAccessorChangeFn(this._checked);
    this.change.emit(event);
  }

  /**
   * Toggles the `checked` value between true and false
   */
  private _toggle() {
    this.checked = !this.checked;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-checkbox',
  template: `<label class="sky-checkbox-wrapper" [ngClass]="{'sky-checkbox-wrapper-disabled': disabled}">
  <input type="checkbox"
        [id]="inputId"
        [checked]="checked"
        [disabled]="disabled"
        [name]="name"
        [tabIndex]="tabindex"
        [attr.aria-label]="label"
        [attr.aria-labelledby]="labelledBy"
        (blur)="onInputBlur()"
        (change)="onInteractionEvent($event)"/>
  <span class="sky-checkbox"></span>
  <ng-content select="sky-checkbox-label"></ng-content>
</label>
`,
  styles: [`.sky-checkbox-wrapper {
  cursor: pointer;
  position: relative;
}

.sky-checkbox-wrapper.sky-checkbox-wrapper-disabled, .sky-checkbox-wrapper.sky-checkbox-wrapper-disabled input {
  cursor: default;
}

.sky-checkbox-wrapper input {
  opacity: 0;
  position: absolute;
  height: 22px;
  width: 22px;
  margin: 0;
  padding: 0;
  cursor: pointer;
}

.sky-checkbox {
  background-color: #ffffff;
  border-top: 1px solid #cdcfd2;
  border-bottom: 1px solid #cdcfd2;
  border-left: 1px solid #cdcfd2;
  border-right: 1px solid #cdcfd2;
  display: inline-block;
  height: 22px;
  margin: 0;
  padding: 0;
  vertical-align: middle;
  width: 22px;
  text-align: center;
  font-size: 15px;
  line-height: 19px;
}

.sky-checkbox-wrapper input:hover + .sky-checkbox {
  border: 2px solid #007ca6;
}

.sky-checkbox-wrapper input:checked + .sky-checkbox {
  background-color: #007ca6;
  border-color: #007ca6;
  border-width: 1px;
}

.sky-checkbox-wrapper input:checked + .sky-checkbox:before {
  color: #ffffff;
  content: "\\f00c";
  font-family: FontAwesome;
  font-size: 13px;
}

.sky-checkbox-wrapper input:disabled + .sky-checkbox {
  background-color: #eeeeef;
  border-top: 1px solid #e2e3e4;
  border-bottom: 1px solid #e2e3e4;
  border-left: 1px solid #e2e3e4;
  border-right: 1px solid #e2e3e4;
}

.sky-checkbox-wrapper input:focus + .sky-checkbox {
  outline: thin dotted;
  outline: -webkit-focus-ring-color auto 5px;
}
`],
  providers: [SKY_CHECKBOX_CONTROL_VALUE_ACCESSOR]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'label': [{ type: Input },],
'labelledBy': [{ type: Input },],
'id': [{ type: Input },],
'disabled': [{ type: Input },],
'tabindex': [{ type: Input },],
'name': [{ type: Input },],
'change': [{ type: Output },],
'checked': [{ type: Input },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
